import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Save, Eye, Check, PenLine, Copy, Layers, Globe, Tags, Monitor, Tablet, Smartphone, ArrowLeft, Trash2, History } from 'lucide-react'
import { DEVICE_WIDTHS } from './BuilderContext'
import { useNavigate, useParams } from 'react-router-dom'
import { useBuilder } from './hooks/useBuilder'
import { useBuilderSave } from './hooks/useBuilderSave'
import { CategoryAutocomplete } from './CategoryAutocomplete'
import { BuilderDuplicateModal } from './BuilderDuplicateModal'
import { BuilderReorderPanel } from './BuilderReorderPanel'

import { BuilderPageAddressPanel } from './BuilderPageAddressPanel'
import { BuilderMetaPanel } from './BuilderMetaPanel'
import { BuilderPageTrashModal } from './BuilderPageTrashModal'
import { fieldsOf, previewModelPath } from './modelPath'
import { useBuilderObjectType } from './hooks/useBuilderObjectTypes'
import { normalizeForSearch } from '../../utils/fuzzy'
import { useOrbitStickyTop } from './hooks/useOrbitStickyTop'
import { BuilderVersionBar, BuilderVersionPanel, VersionCommentField, VersioningToggle } from './BuilderVersions'
import { VERSIONS_CHANGED_EVENT, createVersion, useVersioningChoice } from './versionDiff'
import { notify } from '../../notices'

export const BuilderHeader = ({ initialClone = false, hosted = false }) => {
  const {
    page, setPage, host, isEditMode, setEditMode, isSaving, isDirty, setIsDirty,
    previewDevice, setPreviewDevice, flushAll, contentDirty,
    clearContentDirty, setPagePublished, versionView,
  } = useBuilder()
  const { savePage } = useBuilderSave()
  const navigate = useNavigate()
  // L'URI de la ROUTE : elle peut différer de `page.slug` juste après un
  // changement d'adresse, le temps que la sauvegarde le persiste.
  const { slug: routeSlug } = useParams()
  const [savedFeedback, setSavedFeedback] = useState(false)
  const [saveError, setSaveError] = useState(false)
  // Duplication de la page COURANTE. Ouverte d'emblée quand on est arrivé
  // avec `?clone=1` (bouton « Dupliquer » d'une liste de contenus, qui ne
  // porte pas l'appel de duplication — c'est le builder qui le porte).
  const [showDuplicateModal, setShowDuplicateModal] = useState(initialClone)
  const [showReorderPanel, setShowReorderPanel] = useState(false)

  const [showAddressPanel, setShowAddressPanel] = useState(false)
  const [showMetaPanel, setShowMetaPanel] = useState(false)
  // Mise à la corbeille du CONTENU ENTIER (le node page), pas d'un de ses blocs.
  const [showTrashModal, setShowTrashModal] = useState(false)
  // VERSIONS (cf. BuilderVersions) : réservées à une page du builder déjà
  // enregistrée. Un tableau de bord n'est pas une `builder_page`, et la barre
  // EMBARQUÉE (wiki) a son propre historique éditorial.
  const [showVersionPanel, setShowVersionPanel] = useState(false)
  const [versionCommentOpen, setVersionCommentOpen] = useState(false)
  const canVersion = !host && !hosted && !!page.pageNid
  const versioning = useVersioningChoice(canVersion ? page.pageNid : null)
  const wantsVersion = canVersion && versioning.enabled
  // Sortie d'une consultation (retour à l'édition, restauration) : la barre
  // qui portait le geste disparaît — le focus revient au bouton Historique,
  // d'où l'on était parti, plutôt que de tomber sur <body>.
  const historyButtonRef = useRef(null)
  const wasViewingRef = useRef(false)
  useEffect(() => {
    if (wasViewingRef.current && !versionView) historyButtonRef.current?.focus()
    wasViewingRef.current = !!versionView
  }, [versionView])
  // Grisé tant que des modifications ne sont pas enregistrées — mais en
  // `aria-disabled` : un bouton `disabled` ne prend pas le focus, et sa raison,
  // portée par le seul `title`, restait hors d'atteinte au clavier.
  const openHistory = () => {
    if (dirty) {
      notify({ type: 'info', message: "Enregistrez vos modifications pour consulter l'historique des versions." })
      return
    }
    setShowVersionPanel(true)
  }
  // Le titre se saisit dans le header, mais on ne réécrit l'état de la page
  // (donc tout le canvas) qu'à la validation : une frappe ne doit pas
  // re-rendre les blocs à chaque caractère.
  const [titleDraft, setTitleDraft] = useState(page.title || '')
  useEffect(() => { setTitleDraft(page.title || '') }, [page.title])

  // Le menu Orbit (`.orbit-shell-header`) est LUI AUSSI `sticky top-0`, avec un
  // z-index supérieur : collée à 0, la barre du builder glisserait dessous. On
  // la colle donc sous le menu, à sa hauteur réelle — 0 quand il n'y a pas de
  // menu (builder plein écran) ou qu'il est masqué (layout sidebar). Le détail
  // (menu monté après, bascule header/sidebar, barre embarquée dans un
  // conteneur défilant) vit dans le hook, qui sert aussi la barre de l'éditeur
  // de contenu — une seule mécanique pour les deux.
  const [headerRef, stickyTop] = useOrbitStickyTop()

  // --- Catégories du site portées par la page -------------------------------
  // POURQUOI DU JSX ICI (justification config-first) : le bandeau du builder
  // n'est pas un rjBuild — c'est l'éditeur React, dont l'état de page
  // (`useBuilder`) est la source de vérité et dont l'enregistrement passe par
  // le proxy orchestré `/api/builder/save`. Aucun YAML reactive-json ne s'y
  // monte, et le champ générique `CreatableTagsField` (qui sert la même saisie
  // sur /sites/:id) écrit, lui, par POST partiel immédiat : le brancher ici
  // court-circuiterait le Save et rendrait indéfini l'abandon d'une édition.
  // Ce qui est ajouté est donc du CÂBLAGE (charger le vocabulaire du site,
  // écrire `page.tags`, marquer l'éditeur sale) autour d'un composant qui, lui,
  // existait déjà et reste générique — pas une seconde implémentation du geste.
  //
  // Une catégorie APPARTIENT à un site par le CHAMP `field_site` du terme :
  // une page rattachée à aucun site n'a donc aucun vocabulaire dont se
  // réclamer, et le contrôle disparaît plutôt que de proposer les catégories
  // d'un autre domaine. La liste est une collection decoupled ORDINAIRE
  // (filtrée sur ce champ) — il n'y a pas de point d'API dédié.
  const [siteCategories, setSiteCategories] = useState([])
  const [tagError, setTagError] = useState('')
  useEffect(() => {
    setTagError('')
    if (!page.siteId) {
      setSiteCategories([])
      return undefined
    }
    // Abandonnée si le site change (panneau « Adresse ») avant la réponse :
    // sans cela, une réponse lente réinstallerait les catégories de l'ancien.
    const controller = new AbortController()
    const gid = encodeURIComponent(page.siteId)
    fetch(
      `/decoupled-api/taxonomy_term/site_category/collection?display=decoupled`
      + `&filter[0][f]=field_site&filter[0][v]=${gid}&sort[0][f]=name&sort[0][d]=ASC`,
      { credentials: 'include', signal: controller.signal },
    )
      .then(res => (res.ok ? res.json() : []))
      .then(rows => setSiteCategories(
        // Une ligne est un Syncable ({item_url, data}) pour qui peut écrire, et
        // l'objet à plat pour les autres : decoupled_toolbox n'émet les URLs
        // d'écriture qu'à qui en a le droit. Les deux formes se lisent pareil.
        Array.isArray(rows)
          ? rows.map(r => r?.data ?? r).map(r => ({ id: Number(r.id), label: String(r.name ?? '') }))
          : [],
      ))
      .catch(() => { /* abandon ou panne réseau : le champ reste sans suggestion */ })
    return () => controller.abort()
  }, [page.siteId])

  // La page ne porte que des tid ; le libellé vient du vocabulaire du site. Une
  // catégorie inconnue de la liste (droits, chargement en cours) s'affiche par
  // son id plutôt que de disparaître — un tag muet vaut mieux qu'un tag perdu.
  const pageTags = useMemo(() => (Array.isArray(page.tags) ? page.tags : []), [page.tags])
  const tagValue = useMemo(
    () => pageTags.map(id => siteCategories.find(c => c.id === id) ?? { id, label: `#${id}` }),
    [pageTags, siteCategories],
  )

  // Filtrage LOCAL : le vocabulaire d'un site tient en quelques dizaines de
  // termes et il est déjà chargé. (La médiathèque, elle, interroge le serveur à
  // la frappe — son vocabulaire est commun à tous les sites et bien plus gros.)
  const searchCategories = useCallback(async (query) => {
    const needle = normalizeForSearch(query)
    return needle ? siteCategories.filter(c => normalizeForSearch(c.label).includes(needle)) : siteCategories
  }, [siteCategories])

  // Créer une catégorie est un geste IMMÉDIAT (elle naît rattachée à ce site),
  // là où la POSER sur la page attend le Save comme le titre : sans quoi il
  // faudrait inventer une notion de catégorie « en attente », qu'un abandon de
  // l'édition laisserait dans un état indéfini.
  //
  // Écriture par l'API GÉNÉRIQUE : le terme naît avec son site (`siteIds`), et
  // c'est la contrainte `OrbitSiteCategoryOwner` — pas le front — qui refuse un
  // site où l'on n'a pas le droit d'écrire, ou un nom déjà pris. Le message
  // affiché est celui que le back renvoie.
  const createCategory = async (label) => {
    setTagError('')
    try {
      const res = await fetch('/decoupled-api/save?display=decoupled&entity_type=taxonomy_term&bundle=site_category', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ data: { name: label, siteIds: page.siteId } }),
      })
      const body = await res.json().catch(() => null)
      // L'id d'une entité créée est à la RACINE de la réponse, pas dans `data`.
      if (!res.ok || !body?.id) {
        setTagError(body?.status?.message || 'Création de la catégorie refusée.')
        return null
      }
      const created = { id: Number(body.id), label: String(body.data?.name ?? label) }
      setSiteCategories(prev => (prev.some(c => c.id === created.id) ? prev : [...prev, created]))
      return created
    } catch {
      setTagError('Erreur réseau : catégorie non créée.')
      return null
    }
  }

  const setTags = (ids) => {
    setPage(prev => ({ ...prev, tags: ids }))
    setIsDirty(true)
  }

  // Combien de balises la page renseigne — le bouton dit d'un coup d'œil si des
  // métadonnées existent, sans ouvrir le panneau.
  const metaCount = page.meta ? Object.values(page.meta).filter(v => v !== '' && v != null).length : 0
  // La page ouverte est-elle le MODÈLE d'un type de contenu ? Alors « Adresse »
  // et « Métadonnées » ne portent pas sur elle — elle n'est servie nulle part —
  // mais sur les CONTENUS qu'elle rend. Les deux panneaux le savent ; la barre
  // le dit, pour qu'on ne les ouvre pas en croyant régler autre chose.
  const modelEntry = (Array.isArray(page.modelFor) ? page.modelFor : [])
    .find(entry => entry && entry.objectType) ?? null
  const modelSamplePath = modelEntry ? previewModelPath(modelEntry.path || '', {}) : ''
  // Les champs du type — ce que les balises du modèle peuvent citer. Le hook ne
  // va rien chercher quand il n'y a pas de modèle (nom machine vide).
  const { type: modelType } = useBuilderObjectType(modelEntry?.objectType ?? '')
  const modelTokens = useMemo(
    () => (modelEntry ? fieldsOf(modelType?.definition) : null),
    [modelEntry, modelType],
  )
  // Statut de publication du node page. `undefined` (hôte sans statut, page
  // pas encore chargée) ⇒ publiée, cohérent avec le défaut Drupal.
  const isPublished = page.published !== false
  const dirty = isDirty || contentDirty
  // Le titre est une donnée du node page, écrite par le proxy orchestré : pas
  // de titre éditable pour un hôte non-page (dashboard), dont le save ne
  // persiste que les blocs.
  const titleEditable = isEditMode && !host
  // Retour : un tableau de bord n'a pas de liste de pages derrière lui — on
  // revient à SA vue en lecture (`/dashboards/<id>`), celle d'où l'on vient en
  // cliquant « Éditer ». Sinon : le site qui sert la page, à défaut la liste des
  // pages du builder.
  const dashboardId = host?.kind === 'dashboard' ? host.id : null
  const backTo = dashboardId
    ? `/dashboards/${dashboardId}`
    : page.siteId ? `/sites/${page.siteId}` : '/builder'
  const backLabel = dashboardId
    ? 'Retour au tableau de bord'
    : page.siteId
      ? `Retour au site${page.siteName ? ` « ${page.siteName} »` : ''}`
      : 'Retour à la liste des pages'

  const commitTitle = () => {
    const next = titleDraft.trim()
    // Un titre vide n'a pas de sens côté Drupal (le node le refuserait) :
    // on retombe sur l'ancien plutôt que d'armer une sauvegarde qui échouera.
    if (next === '' || next === page.title) {
      setTitleDraft(page.title || '')
      return
    }
    setPage(prev => ({ ...prev, title: next }))
    setIsDirty(true)
  }

  const handleSave = async () => {
    setSaveError(false)
    try {
      // Flush pending per-unit content first: each existing unit POSTs to its
      // own submission_url (shared-syncable groups coalesce to one). ATTENDU :
      // une version se lit en base juste après — partie avant la fin de ces
      // écritures, elle figerait le contenu d'avant l'édition.
      await flushAll()
      // Only hit the orchestrated proxy when there's STRUCTURAL work — new
      // blocs, reorder, title/slug, modelFor (all tracked by `isDirty`). For a
      // content-only edit the proxy has nothing to do: the unit content is
      // owned by DataSync (flushed above), and calling the proxy here would
      // race that async POST and clobber the just-saved content with its own
      // (concurrent, often stale) render of the page.
      if (isDirty) {
        await savePage()
      }
      clearContentDirty()
      // Version demandée : l'état qu'on vient d'enregistrer devient une version
      // (instantané lu en base par le serveur, cf. PageVersionTrait). Un échec
      // ici n'annule pas le Save — le contenu EST enregistré — mais doit se dire.
      if (wantsVersion) {
        try {
          const created = await createVersion(page.pageNid, versioning.comment.trim())
          notify(created?.unchanged
            ? { type: 'info', message: 'Aucun changement depuis la dernière version : pas de nouvelle version.' }
            : { type: 'success', message: 'Version enregistrée.' })
          versioning.setComment('')
          setVersionCommentOpen(false)
          window.dispatchEvent(new CustomEvent(VERSIONS_CHANGED_EVENT))
        } catch (err) {
          notify({ type: 'error', message: `Contenu enregistré, mais la version n'a pas pu l'être : ${err?.message || 'erreur inconnue'}` })
        }
      }
      setSavedFeedback(true)
      setTimeout(() => setSavedFeedback(false), 2500)
      // L'URI vient de changer : l'éditeur doit suivre, sinon un rechargement
      // ouvrirait `/builder/<ancienne-uri>` — qui ne résout plus rien.
      if (!host && page.slug && routeSlug && page.slug !== routeSlug) {
        navigate(`/builder/${page.slug}`, { replace: true })
      }
    } catch {
      setSaveError(true)
      setTimeout(() => setSaveError(false), 3000)
    }
  }

  // Le contenu vient de partir à la corbeille : on quitte l'éditeur d'un node
  // qui n'existe plus. Les deux drapeaux « sale » sont d'abord retombés — sans
  // ça, l'avertissement « modifications non enregistrées » se déclencherait sur
  // un rechargement, et un enregistrement automatique en attente irait réécrire
  // le contenu supprimé.
  const handleTrashed = () => {
    setIsDirty(false)
    clearContentDirty()
    setShowTrashModal(false)
    // La liste des contenus d'un site est servie par Elasticsearch, qui
    // n'indexera la suppression qu'au `kernel.terminate` de la requête, puis au
    // rafraîchissement de son index (~1 s) : y revenir sèchement y ferait
    // réapparaître une seconde la page qu'on vient de jeter, sans un mot.
    // `pages/site.yaml` sait déjà masquer cette ligne-là et annoncer le succès —
    // il déclare `trashPendingId` / `trashResult` / `trashTitle` dans son `data:`,
    // et TOUTE clé déclarée là est surchargeable par l'URL (contrat générique
    // d'`overlayUrlParams`, cf. components/core/urlDataOverride.js). On lui passe
    // donc l'état, sans une ligne de mécanique nouvelle des deux côtés.
    if (page.siteId) {
      const params = new URLSearchParams({
        trashResult: 'success',
        trashPendingId: String(page.pageNid ?? ''),
        trashTitle: page.title || page.slug || '',
      })
      navigate(`${backTo}?${params}`)
      return
    }
    navigate(backTo)
  }

  // Identité de la page : retour, titre, site de rattachement. En mode
  // modification cette ligne passe AU-DESSUS des boutons (le titre y est
  // saisissable et a besoin de place) ; en prévisualisation elle reste à
  // gauche de la barre.
  const identity = (
    <>
      {/* Retour à la liste : sans objet quand la barre est EMBARQUÉE — on n'est
          pas venu d'une liste du builder, et la colonne de gauche de l'hôte
          porte déjà la navigation. */}
      {!hosted && (
        <button
          onClick={() => navigate(backTo)}
          className="p-1.5 -ml-1.5 rounded-lg text-gray-400 hover:text-teal-700 hover:bg-teal-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
          aria-label={backLabel}
          title={backLabel}
        >
          <ArrowLeft size={16} aria-hidden="true" />
        </button>
      )}
      {/* Titre éditable en place : le champ colle au texte saisi pour que le
          libellé du site reste à son côté. */}
      {titleEditable ? (
        <input
          type="text"
          value={titleDraft}
          onChange={e => setTitleDraft(e.target.value)}
          onBlur={commitTitle}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur() }
            if (e.key === 'Escape') { setTitleDraft(page.title || ''); e.currentTarget.blur() }
          }}
          size={Math.max((titleDraft || '').length + 1, 14)}
          aria-label="Titre de la page"
          placeholder="Titre de la page"
          // En barre COMPACTE (builder embarqué dans le wiki), le champ garde
          // un plancher : flexbox l'écrasait à 18 px, titre illisible débordant
          // par-dessus le bouton voisin. Un titre est saisissable ou ne l'est
          // pas — c'est donc lui qui garde sa place, et les actions qui passent
          // à la ligne. En pleine largeur, `min-w-0` d'origine : la barre y
          // tient sur une ligne et le champ a déjà toute la place voulue.
          className={`${hosted ? 'min-w-[9rem] max-w-lg' : 'min-w-0 max-w-64'} px-2 py-1 text-sm font-medium text-gray-800 bg-transparent border border-transparent rounded-md hover:border-gray-200 focus:border-teal-500 focus:bg-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500`}
        />
      ) : page.title ? (
        <span className="text-gray-700 font-medium text-sm truncate max-w-64" title={page.title}>{page.title}</span>
      ) : null}
      {/* Rattachement courant : la page peut être servie par un domaine
          précis (site) ou par tous (aucun site). En modification, le libellé
          ouvre le panneau « Adresse » qui le règle.
          Rien de tel en barre EMBARQUÉE : une page rangée dans le wiki
          n'appartient à aucun site, et le chip « Aucun site » n'y annonçait
          qu'une évidence, en ouvrant un panneau sans objet. */}
      {hosted ? null : !host && isEditMode ? (
        <button
          onClick={() => setShowAddressPanel(true)}
          className="shrink-0 text-xs text-gray-500 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded hover:border-teal-500 hover:text-teal-700 hover:bg-teal-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
          title={`URI publique et site de rattachement${page.siteName ? ` — site : ${page.siteName}` : ''}`}
        >
          {page.siteName || 'Aucun site'}
        </button>
      ) : page.siteName ? (
        <span className="shrink-0 text-xs text-gray-500 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded">
          {page.siteName}
        </span>
      ) : null}
      {/* Une page MODÈLE n'a pas d'adresse publique : le dire dans la barre,
          sinon rien ne distingue à l'écran un modèle d'une page en ligne — et
          on chercherait longtemps pourquoi son URI répond 404. Le drapeau se
          règle dans le panneau « Adresse », d'où le libellé cliquable. */}
      {!host && page.isTemplate && (
        <span
          className="shrink-0 text-xs font-medium text-teal-800 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded"
          title="Ce contenu est un modèle : il n'est servi nulle part, il sert de point de départ à la création de pages."
        >
          Modèle
        </span>
      )}
      {/* Catégories du site — à CÔTÉ DU TITRE parce que c'est une propriété de
          la page elle-même, au même titre que son site ou son drapeau
          « modèle », et non une action de la barre. Saisie libre : un nom
          inconnu crée sa catégorie, rattachée à ce site. Éditée EN PLACE, sans
          panneau à ouvrir (cf. la décision « inline editing partout »), et
          enregistrée avec la page — comme le titre. */}
      {!host && page.siteId ? (
        isEditMode ? (
          <div className="shrink-0">
            <CategoryAutocomplete
              inline
              value={tagValue}
              onChange={setTags}
              onCreate={createCategory}
              onSearch={searchCategories}
              placeholder="+ catégorie"
              ariaLabel="Catégories de la page"
            />
          </div>
        ) : tagValue.length > 0 ? (
          <ul className="media-lib-ac-tags shrink-0" aria-label="Catégories de la page">
            {tagValue.map(cat => (
              <li key={cat.id} className="media-lib-ac-tag"><span className="truncate">{cat.label}</span></li>
            ))}
          </ul>
        ) : null
      ) : null}
      {/* Panne de la CRÉATION d'une catégorie (droits, réseau) : la poser sur la
          page, elle, ne dit rien ici — c'est le Save qui rend son verdict. */}
      {tagError && <span className="shrink-0 text-xs text-rose-600" role="alert">{tagError}</span>}
    </>
  )

  const actions = (
    <>
      {/* Repartir de CETTE page. Masqué pour un hôte non-page (dashboard) :
          l'endpoint de duplication ne connaît que les `builder_page`, par
          leur slug. Hors modification seulement : la barre d'édition est déjà
          chargée, et dupliquer n'est pas un geste d'édition. */}
      {!host && !isEditMode && page.slug && (
        <button
          onClick={() => setShowDuplicateModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 hover:border-teal-500 hover:text-teal-700 hover:bg-teal-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
          title="Dupliquer cette page"
        >
          <Copy size={14} />
          Clone page
        </button>
      )}

      {saveError && (
        <span className="text-xs text-red-500 font-medium">Save error</span>
      )}

      {isEditMode ? (
        <div className="flex items-center gap-1.5">
          {/* Réordonner, Adresse, Métadonnées : des PICTOS. Avec leurs intitulés,
              la barre ne tenait plus sur une ligne dès qu'on ajoutait les versions
              (1 645 px pour 1 392 disponibles à 1440 px) : elle passait sur deux
              lignes, et l'en-tête collant mangeait le canevas. Ce sont des
              réglages qu'on ouvre rarement — l'intitulé vit dans l'infobulle et le
              nom accessible, l'information portée (compteur de métadonnées) reste
              à l'écran. */}
          <button
            onClick={() => setShowReorderPanel(true)}
            className="p-1.5 rounded-lg text-gray-600 border border-gray-200 hover:border-teal-500 hover:text-teal-700 hover:bg-teal-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
            title="Réordonner les blocs"
            aria-label="Réordonner les blocs"
          >
            <Layers size={15} aria-hidden="true" />
          </button>
          {/* Adresse et Métadonnées règlent la vie PUBLIQUE d'une page
              de site : son URI, son rattachement à un domaine, son
              référencement. Une page du wiki n'a rien de tout ça — elle se lit
              à `/docs/page/<nid>` et n'est servie par aucun domaine. En barre
              embarquée, ces deux boutons ne mènent donc qu'à des réglages sans
              objet : on ne les monte pas. « Publiée » reste : l'état de publication,
              lui, gouverne bien la page. */}
          {!host && !hosted && (
            <>
              <button
                onClick={() => setShowAddressPanel(true)}
                className="p-1.5 rounded-lg text-gray-600 border border-gray-200 hover:border-teal-500 hover:text-teal-700 hover:bg-teal-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                title={modelEntry
                  ? `Adresse des contenus — chemin public des contenus de ce type${modelSamplePath ? ` : ${modelSamplePath}` : ''}`
                  : `Adresse — URI publique et site de rattachement${page.siteName ? ` (site : ${page.siteName})` : ''}`}
                aria-label={modelEntry ? 'Adresse des contenus' : 'Adresse de la page'}
              >
                <Globe size={15} aria-hidden="true" />
              </button>
              <button
                onClick={() => setShowMetaPanel(true)}
                className={`flex items-center gap-1 p-1.5 rounded-lg text-xs font-semibold border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${
                  metaCount > 0
                    ? 'border-teal-300 text-teal-700 bg-teal-50/40 hover:bg-teal-50'
                    : 'border-gray-200 text-gray-600 hover:border-teal-500 hover:text-teal-700 hover:bg-teal-50'
                }`}
                title={modelEntry
                  ? 'Métadonnées des contenus — valeurs par défaut des contenus de ce type, que chaque contenu peut remplacer'
                  : 'Métadonnées — référencement et partage de cette page'}
                aria-label={`${modelEntry ? 'Métadonnées des contenus' : 'Métadonnées de la page'}${metaCount > 0 ? ` (${metaCount} renseignée${metaCount > 1 ? 's' : ''})` : ''}`}
              >
                <Tags size={15} aria-hidden="true" />
                {metaCount > 0 && <span aria-hidden="true">{metaCount}</span>}
              </button>
            </>
          )}
          {!host && (
            <>
              <label
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-teal-500 ${
                  isPublished
                    ? 'border-teal-300 text-teal-700 bg-teal-50/40 hover:bg-teal-50'
                    : 'border-gray-200 text-gray-600 hover:border-teal-500 hover:text-teal-700 hover:bg-teal-50'
                }`}
                title={isPublished
                  ? 'Page publiée — décocher la repasse en brouillon, puis Save'
                  : 'Page en brouillon (visible des seuls éditeurs) — cocher pour la publier, puis Save'}
              >
                <input
                  type="checkbox"
                  checked={isPublished}
                  onChange={(e) => setPagePublished(e.target.checked)}
                  className="w-3.5 h-3.5 accent-teal-700 focus-visible:outline-none"
                />
                Publiée
              </label>
            </>
          )}
          <button
            onClick={() => setEditMode(false)}
            className="p-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
            aria-label="Prévisualiser"
            title="Prévisualiser"
          >
            <Eye size={15} aria-hidden="true" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => setEditMode(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
          aria-label="Switch to edit mode"
        >
          <PenLine size={14} />
          Edit page
        </button>
      )}

      <div className="flex items-center bg-gray-100 rounded-lg p-0.5" role="group" aria-label="Device preview">
        {[
          { id: 'mobile', Icon: Smartphone, label: 'Mobile' },
          { id: 'tablet', Icon: Tablet, label: 'Tablet' },
          { id: 'desktop', Icon: Monitor, label: 'Desktop' },
        ].map(d => (
          <button
            key={d.id}
            onClick={() => setPreviewDevice(d.id)}
            className={`p-1.5 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${
              previewDevice === d.id
                ? 'bg-white shadow-sm text-teal-700'
                : 'text-gray-400 hover:text-gray-600'
            }`}
            aria-label={d.label}
            title={`${d.label} (${DEVICE_WIDTHS[d.id]})`}
          >
            <d.Icon size={15} />
          </button>
        ))}
      </div>

      {/* Historique des versions : consultable en aperçu comme en édition. Fermé
          tant que des modifications ne sont pas enregistrées — consulter une
          version démonte les blocs édités, et la « version actuelle » qu'on y
          compare est l'état EN BASE. */}
      {canVersion && (
        <button
          type="button"
          ref={historyButtonRef}
          onClick={openHistory}
          aria-disabled={dirty}
          className={`p-1.5 rounded-lg border border-gray-200 text-gray-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${dirty ? 'opacity-50 cursor-not-allowed' : 'hover:border-teal-500 hover:text-teal-700 hover:bg-teal-50'}`}
          aria-label="Historique des versions"
          title={dirty ? 'Enregistrez vos modifications pour consulter l\'historique' : 'Historique des versions'}
        >
          <History size={15} aria-hidden="true" />
        </button>
      )}

      {isEditMode && canVersion && (
        <VersioningToggle
          choice={versioning}
          commentOpen={versionCommentOpen}
          onToggleComment={() => setVersionCommentOpen(o => !o)}
        />
      )}

      {(page.slug || host) && (
        <button
          onClick={handleSave}
          // Version demandée : Save reste disponible sans modification en cours —
          // c'est alors le geste qui fige l'état enregistré en version.
          disabled={isSaving || (!dirty && !wantsVersion)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 ${
            savedFeedback
              ? 'bg-green-600 text-white'
              : dirty || wantsVersion
                ? 'bg-gray-800 text-white hover:bg-gray-900'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
          aria-label={wantsVersion ? 'Save — enregistrer la page et créer une version' : 'Save page'}
          title={wantsVersion ? 'Enregistre la page et en crée une version' : undefined}
        >
          {savedFeedback ? <Check size={14} /> : <Save size={14} />}
          {isSaving ? 'Saving…' : savedFeedback ? 'Saved!' : 'Save'}
        </button>
      )}
      {/* Mettre TOUT LE CONTENU à la corbeille, sans repasser par la liste des
          contenus du site. Le geste destructif est le DERNIER de la barre,
          isolé par un filet : on ne le rencontre pas en visant Prévisualiser
          ou Save. Écarté quand la barre est embarquée (le wiki a sa propre
          action de suppression, sur l'arborescence) ou quand l'hôte n'est pas
          une page (un tableau de bord n'est pas un node `builder_page` : son
          nid n'est pas `page.pageNid`). Une page pas encore enregistrée n'a
          rien à supprimer.
          Le picto n'est PAS conditionné à un droit calculé ici : c'est Drupal
          qui refuse, et la pop-in qui le raconte. */}
      {isEditMode && !host && !hosted && page.pageNid && (
        <>
          <span className="mx-0.5 h-5 w-px bg-gray-200" aria-hidden="true" />
          <button
            type="button"
            onClick={() => setShowTrashModal(true)}
            className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:border-rose-300 hover:text-rose-700 hover:bg-rose-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
            aria-label="Mettre ce contenu à la corbeille"
            title="Mettre ce contenu à la corbeille"
          >
            <Trash2 size={15} aria-hidden="true" />
          </button>
        </>
      )}
    </>
  )

  return (
    <>
    <header
      id="builder-header"
      ref={headerRef}
      className="sticky z-30 bg-white border-b border-gray-200 px-6 py-3 shadow-sm"
      style={{ top: stickyTop }}
    >
      {/* Le groupe de gauche (`min-w-0`) se laisse écraser sous la largeur de son
          contenu — ses enfants sont `shrink-0` — et le titre passe alors PAR-DESSUS
          les boutons voisins, qui ne se poussent pas. D'où le retour à la ligne.
          Il ne coûte rien en pleine largeur (`flex-wrap` ne wrappe que si ça ne
          tient pas) et il est nécessaire dans les DEUX cas où la barre est
          étroite : le shell du wiki (`hosted`, ~900 px), et depuis que
          l'assistant s'ouvre en COLONNE, la page de builder elle-même. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">{identity}</div>
        {/* Pendant la consultation d'une version, ses issues REMPLACENT les
            actions d'édition : rien de ce qu'elles font ne s'applique à une
            version (on ne l'enregistre pas, on ne la réordonne pas). */}
        {versionView
          ? <BuilderVersionBar onOpenHistory={() => setShowVersionPanel(true)} />
          : <div className="flex items-center gap-1.5 shrink-0">{actions}</div>}
      </div>
      {!versionView && isEditMode && wantsVersion && versionCommentOpen && (
        <VersionCommentField choice={versioning} onSubmit={handleSave} />
      )}
    </header>

    {showDuplicateModal && page.slug && (
      <BuilderDuplicateModal page={page} onClose={() => setShowDuplicateModal(false)} />
    )}
    {showReorderPanel && <BuilderReorderPanel onClose={() => setShowReorderPanel(false)} />}
    {showVersionPanel && canVersion && <BuilderVersionPanel onClose={() => setShowVersionPanel(false)} />}

    {showAddressPanel && <BuilderPageAddressPanel onClose={() => setShowAddressPanel(false)} />}
    {/* Sur un MODÈLE, le panneau édite toujours `page.meta` — c'est bien le
        champ de cette page —, mais ce qu'il décrit, ce sont les contenus : ses
        balises ne sont servies qu'à travers eux, en DÉFAUT. On ne change donc
        pas la mécanique, on change ce que l'écran en dit. */}
    {showMetaPanel && (
      <BuilderMetaPanel
        onClose={() => setShowMetaPanel(false)}
        heading={modelEntry ? 'Métadonnées des contenus' : undefined}
        urlPreview={modelEntry ? (modelSamplePath || undefined) : undefined}
        titleFallback={modelEntry ? 'Titre du contenu' : undefined}
        tokens={modelTokens && modelTokens.length > 0 ? modelTokens : undefined}
        intro={modelEntry ? (
          <>
            Cette page est un <strong>modèle</strong> : elle n&apos;est servie nulle part.
            Ces balises sont le <strong>gabarit</strong> de tous les contenus qu&apos;elle
            rend — citez leurs champs avec <code className="font-mono">@</code> pour que
            chacun obtienne les siennes. Un contenu peut ensuite les remplacer, champ par
            champ, depuis son propre panneau « Métadonnées » ; ce qui reste vide des deux
            côtés reprend le titre du contenu suivi du nom du site.
          </>
        ) : undefined}
      />
    )}
    {showTrashModal && (
      <BuilderPageTrashModal
        page={page}
        dirty={dirty}
        onClose={() => setShowTrashModal(false)}
        onTrashed={handleTrashed}
      />
    )}
  </>
  )
}
